import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Wrench, BookText, History, MessageSquarePlus, Settings as SettingsIcon, Store } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { UserMenu } from "@/components/auth/UserMenu";
import { TesseraAccesso } from "@/components/account/TesseraAccesso";
import { useAuth } from "@/hooks/useAuth";
import { DemoBadge } from "@/components/chat/DemoBadge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useConversations } from "@/hooks/useConversations";
import { useBanco } from "@/hooks/useBanco";
import { cn } from "@/lib/utils";

interface MobileSidebarDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileSidebarDrawer({ open, onOpenChange }: MobileSidebarDrawerProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { items } = useConversations();
  const { user, profile } = useAuth();
  const banco = useBanco();

  const recents = items.slice(0, 20);
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="flex w-72 flex-col gap-0 border-r border-border/60 bg-surface/95 p-0"
        style={{
          paddingTop: "var(--safe-top, 0px)",
          paddingBottom: "var(--safe-bottom, 0px)",
        }}
      >
        <div className="flex items-center gap-2 px-3 pt-3 pb-3">
          <div className="min-w-0 flex-1 pl-1">
            <Logo size="sm" showWordmark />
          </div>
        </div>

        <div className="px-3 pb-2">
          <Button
            onClick={() => {
              navigate("/chat?new=1");
              close();
            }}
            className="w-full justify-start gap-2"
            variant="secondary"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Nuova chat
          </Button>
        </div>

        {recents.length > 0 && (
          <div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Conversazioni recenti
          </div>
        )}
        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {recents.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              <p>Nessuna conversazione ancora.</p>
              <p className="mt-1 text-xs">
                Scegli una domanda rapida o scrivi la tua per iniziare.
              </p>
            </div>
          ) : (
            <ul className="space-y-0.5">
              {recents.map((c) => {
                const active = pathname === `/chat/${c.id}`;
                return (
                  <li key={c.id}>
                    <Link
                      to={`/chat/${c.id}`}
                      onClick={close}
                      className={cn(
                        "block truncate rounded-md px-2 py-1.5 text-sm transition-colors",
                        active
                          ? "bg-primary/15 text-foreground"
                          : "text-muted-foreground hover:bg-surface hover:text-foreground",
                      )}
                    >
                      {c.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* Con la barra in basso in pensione, lo Storico entra qui: le
            "recenti" qui sopra sono un assaggio, la pagina e' l'archivio
            completo con cancellazione (Davide, 26/08: «come ci si arriva?»). */}
        <div className="border-t border-border/60 px-2 py-2">
          <NavLink
            to="/history"
            onClick={close}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-accent/30 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <History className="h-4 w-4" />
            Storico
          </NavLink>
          <NavLink
            to="/strumenti"
            onClick={close}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-accent/30 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <Wrench className="h-4 w-4" />
            Strumenti
          </NavLink>
          {/* Solo per chi Datacom ha concesso la chat (come i marchi) o ha
              una richiesta di tecnico aperta; chi risponde vede la postazione. */}
          {banco.operatore && (
            <NavLink
              to="/banco-operatore"
              onClick={close}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-accent/30 text-foreground"
                    : "text-muted-foreground hover:bg-surface hover:text-foreground",
                )
              }
            >
              <Store className="h-4 w-4" />
              Postazione banco
            </NavLink>
          )}
          {!banco.operatore && banco.cliente && (
            <NavLink
              to="/banco"
              onClick={close}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-accent/30 text-foreground"
                    : "text-muted-foreground hover:bg-surface hover:text-foreground",
                )
              }
            >
              <Store className="h-4 w-4" />
              Banco Datacom
            </NavLink>
          )}
          <NavLink
            to="/aliases"
            onClick={close}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-accent/30 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <BookText className="h-4 w-4" />
            Glossario
          </NavLink>
          <NavLink
            to="/settings"
            onClick={close}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                isActive
                  ? "bg-accent/30 text-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground",
              )
            }
          >
            <SettingsIcon className="h-4 w-4" />
            Impostazioni
          </NavLink>
        </div>

        <div className="border-t border-border/60 px-2 py-1.5">
          <TesseraAccesso compatta onNaviga={close} />
        </div>

        {/* L'account sta qui, non in testata: lassu' lo spazio serve a
            Strumenti e Nuova chat (Davide, 26/08). */}
        <div className="border-t border-border/60 px-3 py-3">
          <div className="flex items-center gap-2.5">
            <UserMenu size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">
                {profile?.first_name || profile?.last_name
                  ? `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim()
                  : "Account"}
              </div>
              {user?.email && (
                <div className="truncate text-xs text-muted-foreground">{user.email}</div>
              )}
            </div>
          </div>
          <div className="mt-2.5">
            <DemoBadge />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}